import { notificationService } from '../Services/NotificationService';

// Web Push helpers. Flow:
//   1. register public/push-sw.js
//   2. ask the user for notification permission (must be from a click on iOS)
//   3. subscribe with the server's VAPID public key
//   4. send the subscription to the API so it can push to this device
//
// Requirements to keep in mind:
//   - HTTPS (or localhost). Push is unavailable on plain http.
//   - iPhone/iPad: iOS 16.4+, and the site must be added to the Home Screen
//     (Share -> Add to Home Screen) and opened from that icon. In a normal
//     Safari tab `PushManager` simply doesn't exist.

const SW_URL = `${process.env.PUBLIC_URL || ''}/push-sw.js`;

export const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const getPermission = () => (isPushSupported() ? Notification.permission : 'unsupported');

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

async function getRegistration() {
  await navigator.serviceWorker.register(SW_URL);
  return navigator.serviceWorker.ready;
}

async function saveSubscription(subscription, laboratoryId, userId) {
  const json = subscription.toJSON();
  await notificationService.subscribePush({
    laboratoryId: Number(laboratoryId),
    userId: Number(userId),
    endpoint: json.endpoint,
    keys: json.keys
  });
}

// Call from a click handler. Returns the resulting permission state.
export async function enablePush(laboratoryId, userId) {
  if (!isPushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  const registration = await getRegistration();
  const { publicKey } = await notificationService.getPushPublicKey();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  }

  await saveSubscription(subscription, laboratoryId, userId);
  return 'granted';
}

// Call on app load when permission is already granted: re-registers the
// worker and re-sends the subscription, so the server always maps this
// device to whoever is currently logged in on it. Never prompts.
export async function syncPush(laboratoryId, userId) {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  try {
    const registration = await getRegistration();
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await saveSubscription(subscription, laboratoryId, userId);
    } else {
      // Permission still granted but the browser dropped the subscription
      // (e.g. it expired) - silently create a new one.
      await enablePush(laboratoryId, userId);
    }
  } catch (error) {
    console.error('Failed to sync push subscription:', error);
  }
}

// Call on logout, BEFORE the auth token is cleared, so a shared/handed-over
// device stops ringing for the previous user.
export async function disablePush() {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_URL);
    const subscription = registration && (await registration.pushManager.getSubscription());
    if (!subscription) return;
    await notificationService.unsubscribePush(subscription.endpoint).catch(() => {});
    await subscription.unsubscribe();
  } catch (error) {
    console.error('Failed to disable push:', error);
  }
}
